import { type ChatStateThread, useChatStore } from "@/store/chat.store";
import { ChatService } from "@/services/socket/chat.service";
import type { WSConnection } from "@/plugins/ws.connection";
import type { UpdateThreadRequest } from "@/api/chat/dto/request/update-thread.request";
import { useCache } from "@/composables/useCache";
import { useAppStore } from "@/store/app.store";
import { API } from "@/api/api";
import dayjs from "dayjs";

export function useChat() {
  const chatStore = useChatStore();
  const appStore = useAppStore();
  const api = new API();
  const {
    getCachedThreads,
    upsertCachedThread,
    setThreadsTimeFrom,
    getThreadsTimeFrom,
    getUseCache,
    clearCachedThreads,
  } = useCache();

  const viewThread = (thread: ChatStateThread, limit = 25, timeFrom: number | null = null) => {
    if (!thread || !thread.id || !chatStore.chatService) {
      return;
    }

    chatStore.setViewedThreadId(thread.id);
    chatStore.chatService.sendMessagesUpdate(thread.id, limit, timeFrom);
  };

  const updateThread = async (thread: ChatStateThread, data: UpdateThreadRequest) => {
    try {
      const resp = await api.chat.updateThread(thread.id, data);
      if (resp.success) {
        viewThread(thread);
      }
    } catch (err) {
      console.error("Can not update thread", err);
    }
  };

  const noResponseThread = async (thread: ChatStateThread) => {
    try {
      const resp = await api.chat.noResponseThread(thread.id);
      if (resp.success) {
        viewThread(thread);
      }
    } catch (err) {
      console.error("Can not set no response thread", err);
    }
  };

  const resolveThread = async (thread: ChatStateThread) => {
    await updateThread(thread, { dispatcherId: appStore.dispatcher?.id ?? undefined, resolved: true });
  };

  const loadAllThreads = async () => {
    let offset = 0;
    const useCacheFlag = getUseCache();
    const timeFrom = getThreadsTimeFrom();
    const timeFromUnix = timeFrom && useCacheFlag ? dayjs(timeFrom).unix().toString() : null;

    for (;;) {
      try {
        const newTimeFrom = dayjs().toISOString();
        const resp = await api.chat.getThreads(offset, 300, timeFromUnix);

        if (!resp.success || !resp.data) {
          return;
        }

        if (resp.data.threads.length === 0) {
          if (useCacheFlag) {
            await getCachedThreads();
            setThreadsTimeFrom(newTimeFrom);
          } else {
            const cachedThreads = await getCachedThreads(false);

            if (cachedThreads.length) {
              await clearCachedThreads();
            }
          }

          break;
        }

        if (useCacheFlag) {
          await Promise.all(resp.data.threads.map((thread) => upsertCachedThread(thread, false)));
        } else {
          chatStore.upsertThreads(resp.data.threads);
        }

        offset += resp.data.threads.length;
      } catch (e) {
        console.error("Can t update threads list", e);
        break;
      }
    }

    chatStore.sortThreads();
    chatStore.recalcThreadsCount();
  };

  const handleChatSocket = (connection: WSConnection) => {
    const chatService = new ChatService(connection);
    chatStore.setChatService(chatService);
    chatService.subscribe();
  };

  const disconnectChat = () => {
    if (chatStore.chatService) {
      chatStore.chatService.unsubscribe();
      chatStore.chatService = null;
    }
  };

  return {
    viewThread,
    updateThread,
    noResponseThread,
    resolveThread,
    loadAllThreads,
    handleChatSocket,
    disconnectChat,
  };
}
